import { useMsal } from '@azure/msal-react';
import { useMemo } from 'react';

import { Role, principalFromClaims, resolveRole } from './roles';
import type { IraClaims, Principal, RoleValue } from './roles';
import { useRoles } from './useRoles';

const ANONYMOUS: Principal = {
  subject: '',
  upn: '',
  name: '',
  role: Role.NONE,
  tenantId: '',
  rawRoles: [],
};

/**
 * The signed-in principal: identity from the ID token, role from the API access token.
 *
 * The split is not a stylistic one. Identity claims — `oid`, `tid`, `name`, the UPN — are
 * what the ID token exists to carry, and reading them from the cached account needs no
 * network call. The `roles` claim is not there at all, because app roles belong to the API
 * registration and Entra only stamps them on tokens issued for it; `useRoles` explains why
 * that is worth an extra token acquisition rather than defining the roles twice.
 */
export function usePrincipal(): Principal {
  const { accounts } = useMsal();
  const account = accounts[0];
  const { roles } = useRoles();

  return useMemo(() => {
    if (!account?.idTokenClaims) return ANONYMOUS;
    const claims = account.idTokenClaims as IraClaims;
    const principal = principalFromClaims(claims);
    return {
      ...principal,
      role: resolveRole(roles),
      rawRoles: roles,
      // The account's own username is more reliable than the optional UPN claims, which
      // depend on the app manifest's optional-claims configuration.
      upn: principal.upn || (account.username ?? ''),
    };
  }, [account, roles]);
}

/** Convenience for the common "is this control available" check. */
export function useHasRole(minimum: RoleValue): boolean {
  const principal = usePrincipal();
  return principal.role >= minimum;
}
